import React from 'react';
import { CombinedImage } from '@ynap/image';
import HtmlVideoPlayer from '@ynap/video';
import ViewportObserver from '@ynap/viewport-observer';

interface Image {
    id: string;
    url: string;
    alt: string;
    srcSet?: string;
}

interface Thumbnail {
    alt: string;
    url: string;
}

interface Video {
    url: string;
    thumbnail?: Thumbnail;
}

interface GalleryContent {
    images: Image[];
    videos: Video[];
    isVideoFirst: boolean;
    isLargeDesktop: boolean;
    /* eslint-disable no-unused-vars */
    setIsZoomModalVisible: (visible: boolean) => void;
    setSelectedZoomImageIndex: (index: number) => void;
    bem: (className: string) => string | undefined;
    /* eslint-enable no-unused-vars */
}

export const buildGalleryContent = ({
    images,
    videos,
    isVideoFirst,
    isLargeDesktop,
    setIsZoomModalVisible,
    setSelectedZoomImageIndex,
    bem,
}: GalleryContent) => {
    const assertedImages = images.map((image, i) => {
        return {
            video: false,
            ...image,
            component: (
                <div
                    onClick={() => {
                        setIsZoomModalVisible(true);
                        setSelectedZoomImageIndex(i);
                    }}
                    className={bem('imageContainer')}
                >
                    <CombinedImage
                        key={image.id}
                        src={image.url}
                        alt={image.alt}
                        primarySrcset={image.srcSet}
                        displaySrc
                        yOffset={'100%'}
                        withIntersectionObserver={i > 1} //remove intersection observer for first image
                        cover
                    />
                </div>
            ),
        };
    });

    const assertedVideos = videos.map((video) => {
        return {
            video: true,
            ...video,
            component: (
                <ViewportObserver threshold={[0.5]} keepObserving={true}>
                    {(intersected: boolean) => {
                        return <HtmlVideoPlayer video={video} key={video.url} intersected={intersected} className={bem('video')} />;
                    }}
                </ViewportObserver>
            ),
        };
    });

    const [firstImage, ...rest] = assertedImages;

    if (!assertedVideos.length) return assertedImages;

    if (isVideoFirst && isLargeDesktop) {
        return [assertedVideos[0], ...rest];
    } else if (isVideoFirst && !isLargeDesktop) {
        return [firstImage, assertedVideos[0], ...rest];
    } else if (!isVideoFirst && !isLargeDesktop) {
        return [firstImage, ...rest, assertedVideos[0]];
    } else {
        return [...rest, assertedVideos[0]];
    }
};
